import React, { useState } from 'react';
import { Form, Input, Modal, Row, Col } from 'antd';
import { Button } from '../../components/ui/button';
import { UserOutlined, MailOutlined, LockOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useRegister } from '../../hooks/useAuth';
import { ValueProposition } from './ValueProposition';

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 4rem;
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const FormWrapper = styled.div`
  width: 450px;
  margin-top: 90px;
  background-color: #fff;
  padding: 20px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  border-radius: 8px;

  h2,
  h5 {
    text-align: center;
    margin: 0px 0px 8px 0px;
  }
  h5 {
    margin: 0px 0px 2em 0px;
  }
`;

type RegisterFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  company?: string;
  password: string;
  confirmPassword: string;
};

export const Register: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const nav = useNavigate();

  const handleModal = () => {
    nav('/login');
    setShowModal(false);
  };

  const { mutate, isPending, contextHolder } = useRegister({
    onSuccess: () => {
      setShowModal(true);
    },
  });

  const onFinish = (values: RegisterFormValues) => {
    const { firstName, lastName, email, company, password, confirmPassword } =
      values;

    if (!firstName || !lastName || !email || !password || !confirmPassword) {
      return;
    }

    if (password !== confirmPassword) {
      // You could also handle this with form validation instead
      return;
    }

    mutate({ firstName, lastName, email, company, password });
  };

  return (
    <>
      {contextHolder}
      <Container>
        <ValueProposition />
        <FormWrapper>
          <div className="text-1xl mb-1 text-center font-bold tracking-tighter sm:text-2xl">
            Create Your Account
          </div>
          <p className="mb-6 text-center text-sm text-gray-600 text-secondary-foreground">
            Start exploring our address database today
          </p>
          <Form
            name="register"
            onFinish={onFinish}
            initialValues={{ remember: true }}
            scrollToFirstError
            disabled={isPending}
            validateTrigger={['onSubmit']}
          >
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item
                  name="firstName"
                  rules={[
                    {
                      required: true,
                      message: 'Please input your first name!',
                    },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined className="site-form-item-icon" />}
                    placeholder="First Name"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="lastName"
                  rules={[
                    { required: true, message: 'Please input your last name!' },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined className="site-form-item-icon" />}
                    placeholder="Last Name"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={8}>
              <Col span={12}>
                <Form.Item
                  name="email"
                  rules={[
                    {
                      type: 'email',
                      message: 'The input is not valid E-mail!',
                    },
                    {
                      required: true,
                      message: 'Please input your E-mail!',
                    },
                  ]}
                >
                  <Input
                    prefix={<MailOutlined className="site-form-item-icon" />}
                    placeholder="Email"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="company"
                  rules={[
                    {
                      required: false,
                      message: 'Please input your company name!',
                    },
                  ]}
                >
                  <Input
                    prefix={<UserOutlined className="site-form-item-icon" />}
                    placeholder="Company"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="password"
              rules={[
                {
                  required: true,
                  message: 'Please input your password!',
                },
              ]}
            >
              <Input
                prefix={<LockOutlined className="site-form-item-icon" />}
                type="password"
                placeholder="Password"
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              rules={[
                {
                  required: true,
                  message: 'Please confirm your password!',
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match!'));
                  },
                }),
              ]}
            >
              <Input
                prefix={<LockOutlined className="site-form-item-icon" />}
                type="password"
                placeholder="Confirm Password"
              />
            </Form.Item>

            <Form.Item>
              <Button
                variant="default"
                type="submit"
                className="w-full font-bold"
                disabled={isPending}
              >
                {isPending
                  ? 'Creating Account...'
                  : 'Create Account & Start Searching'}
              </Button>
            </Form.Item>

            <div className="text-center">
              <p className="mb-2 text-secondary-foreground">
                Already have an account?{' '}
                <Link to="/login" className="text-primary hover:underline">
                  Sign in
                </Link>
              </p>
              <p className="text-xs text-muted-foreground">
                By signing up, you agree to our{' '}
                <a
                  href="https://www.elementus.io/legal/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  href="https://www.elementus.io/legal/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  Privacy Policy
                </a>
              </p>
            </div>
          </Form>
        </FormWrapper>
      </Container>

      <Modal
        title="Registration Successful"
        open={showModal}
        onCancel={handleModal}
        footer={[
          <Button key="submit" variant="default" onClick={handleModal}>
            Ok
          </Button>,
        ]}
      >
        <p>Please check your email to verify your account.</p>
      </Modal>
    </>
  );
};
